/**
 * Programmatic appointment display title for the appointment page, calendar modal,
 * and calendar cards: `{Client} — {Episode}`. Group names stand alone.
 */

export const APPOINTMENT_TITLE_SEPARATOR = ' — ';

export function buildAppointmentDisplayTitle(input: {
  /** Group / cohort appointment name — when set, used alone. */
  groupDisplayName?: string | null;
  episodeDisplayName?: string | null;
  clientDisplayName?: string | null;
  /** Fallback when neither group nor EoC/client parts are available. */
  fallback?: string;
}): string {
  const group = input.groupDisplayName?.trim();
  if (group) return group;

  const episode = input.episodeDisplayName?.trim();
  const client = input.clientDisplayName?.trim();
  if (episode && client) return `${client}${APPOINTMENT_TITLE_SEPARATOR}${episode}`;
  if (client) return client;
  if (episode) return episode;
  return input.fallback?.trim() || 'Appointment';
}

/** Split `{Client} — {Episode}` so the client name can be a chart link. */
export function splitAppointmentDisplayTitle(
  title: string,
  clientDisplayName?: string | null,
): { clientName?: string; rest: string } {
  const client = clientDisplayName?.trim();
  if (!client) return { rest: title };
  if (title === client) return { clientName: client, rest: '' };
  const prefix = `${client}${APPOINTMENT_TITLE_SEPARATOR}`;
  if (title.startsWith(prefix)) {
    return { clientName: client, rest: title.slice(prefix.length) };
  }
  return { rest: title };
}

/**
 * `{Client} — {Episode}` for the calendar editor. Uses caseload episode text first,
 * then catalog type name, then the rest of an existing title, then session type —
 * so the header is never client-only when any episode/session label is known.
 */
export function resolveSessionAppointmentTitle(input: {
  clientDisplayName?: string | null;
  episodeDisplayName?: string | null;
  episodeTypeName?: string | null;
  sessionTypeDisplay?: string | null;
  existingTitle?: string | null;
  fallback?: string;
}): string {
  const client = input.clientDisplayName?.trim() || undefined;
  const fromExisting = splitAppointmentDisplayTitle(input.existingTitle ?? '', client).rest.trim();
  const episode =
    input.episodeDisplayName?.trim()
    || input.episodeTypeName?.trim()
    || (fromExisting || undefined)
    || input.sessionTypeDisplay?.trim()
    || undefined;
  return buildAppointmentDisplayTitle({
    clientDisplayName: client,
    episodeDisplayName: episode,
    fallback: input.fallback?.trim() || 'Session',
  });
}

